/*
TODO

- Add ADMIN role
- ADMIN: [PERMISSIONS.USER, PERMISSIONS.ADMIN]
- Add ADMIN condition in isAllowed BEFORE user condition to ensure `true` is returned before next role checks
*/

import { Permission, Role } from './models/Permissions';
import { UserModel } from './models/User';
import type { RbacClaim } from './models/Rbac';

const ROLES: { [key: string]: Role } = {
  USER: 'user',
  ADMIN: 'admin',
  SUPERKUDU: 'superKudu',
  ARCHITECT: 'architect',
};

// roles allowed per profile
export const PROFILES_PERMISSIONS: { [key: string]: Role[] } = {
  USER: [ROLES.USER, ROLES.SUPERKUDU, ROLES.ADMIN, ROLES.ARCHITECT],
  ARCHITECT: [ROLES.ARCHITECT, ROLES.SUPERKUDU, ROLES.ADMIN],
  SUPERKUDU: [ROLES.SUPERKUDU],
};

export function shouldAllowForTenant(
  tenant: number,
  userPermissions: Permission[],
  allowedRoles: Role[]
): boolean {
  return !!userPermissions.find((permission) => {
    return (
      Number(permission.tenant) === Number(tenant) &&
      allowedRoles.includes(permission.role)
    );
  });
}

export function extractAllowedTenantsOfUser(user: UserModel): number[] {
  return user.permissions.map((permission) => {
    return permission.tenant;
  });
}

export function checkIsArchitect(
  tenant: number,
  userPermissions: Permission[] | undefined
): boolean {
  // Service-to-service (api-key / superKudu) callers have no permissions array;
  // a missing list means "no matching permission", not a crash on .find.
  return !!(userPermissions ?? []).find((permission) => {
    return (
      permission.tenant === tenant &&
      (permission.role === 'architect' || permission.role === 'admin')
    );
  });
}

/**
 * An RBAC user's Data Studio seat: an Org Admin, or a Standard user holding the
 * module, on a tenant that bought Data Studio. Read-only users never hold one.
 */
export function hasDataStudioSeat(rbac: RbacClaim): boolean {
  return (
    rbac.purchasedModules.includes('data_studio') &&
    (rbac.role === 'orgAdmin' ||
      (rbac.role === 'standard' &&
        rbac.assignedModules.includes('data_studio')))
  );
}

type TenantUser = {
  permissions?: Permission[];
  rbac?: RbacClaim | null;
};

/**
 * Whether `user` passes a role profile on `tenant`. An RBAC user (rbac claim) is
 * decided by the claim: a Data Studio seat counts as an architect, so it passes
 * the USER and ARCHITECT profiles, and anything else holds no role. They still
 * need a membership on the tenant. Users without a claim keep their legacy roles.
 */
export function allowsForTenant(
  user: TenantUser,
  tenant: number,
  allowedRoles: Role[]
): boolean {
  if (!user.rbac) {
    return shouldAllowForTenant(tenant, user.permissions, allowedRoles);
  }
  return (
    hasDataStudioSeat(user.rbac) &&
    allowedRoles.includes(ROLES.ARCHITECT) &&
    (user.permissions ?? []).some(
      (permission) => Number(permission.tenant) === Number(tenant)
    )
  );
}

/** `checkIsArchitect`, with RBAC users decided by their claim (see `allowsForTenant`). */
export function isArchitectFor(user: TenantUser, tenant: number): boolean {
  return user.rbac
    ? allowsForTenant(user, tenant, PROFILES_PERMISSIONS.ARCHITECT)
    : checkIsArchitect(tenant, user.permissions);
}

/**
 * An RBAC read-only user: Market Analyzer only, refused all of Data Studio.
 * SuperKudu, API-key callers and v1 users (no rbac claim) never are.
 */
export function isReadOnlyUser(
  user: Pick<UserModel, 'superKudu' | 'rbac'> | undefined
): boolean {
  return !!user && !user.superKudu && user.rbac?.role === 'readOnly';
}
